import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import { currentTopicSettings } from '../state/topicSettings';
import { usedTopicCount } from '../utils/topicUsage';

/**
 * "3 / 5 topic tags used", next to the submit button.
 *
 * The design puts the counter in the composer's footer, on the opposite side
 * from the submit button, and turns it red when the post is full. It is the
 * only place the user can see the quota before hitting the limit, because the
 * suggestions simply stop appearing once it is used up.
 *
 * The composer does not redraw while you type, so this component watches the
 * composer's content stream itself and asks for a redraw when the number
 * changes.
 */
export default class TopicCounter extends Component {
  /**
   * The derived stream that tells us the content changed. Mithril's streams
   * have no "subscribe", but mapping one keeps it up to date.
   */

  lastUsed = (() => -1)();
  oncreate(vnode) {
    super.oncreate(vnode);
    this.watch();
  }
  onupdate(vnode) {
    super.onupdate(vnode);

    // The composer replaces its content stream every time it is cleared, so
    // the subscription has to be checked, not just made once.
    this.watch();
  }
  onremove(vnode) {
    super.onremove(vnode);
    this.derived = undefined;
    this.watched = undefined;
  }

  /**
   * Subscribe to the composer's content, if we are not already subscribed to
   * the stream the composer currently has.
   */
  watch() {
    const stream = app.composer?.fields?.content;
    if (!stream || stream === this.watched) return;
    this.watched = stream;

    /**
     * Mithril's streams have no subscribe(): mapping one keeps it in step
     * with the source, and it goes quiet when the source is replaced (which
     * is what happens when the composer is cleared).
     */
    this.derived = stream.map(() => this.refresh());
  }

  /**
   * Redraw, but only when the number on screen would actually change.
   */
  refresh() {
    const used = usedTopicCount(this.content());
    if (used === this.lastUsed) return;
    this.lastUsed = used;
    m.redraw();
  }
  content() {
    return app.composer?.fields?.content?.() ?? '';
  }
  view(vnode) {
    const settings = currentTopicSettings();
    const max = settings.maxPerPost;

    // The feature is switched off: there is nothing to count.
    if (max <= 0) return null;
    const used = usedTopicCount(this.content());
    const full = used >= max;
    this.lastUsed = used;
    return m("span", {
      className: 'TopicCounter' + (full ? ' TopicCounter--full' : '')
    }, m("span", {
      className: "TopicCounter-text"
    }, this.prefix(), m("b", {
      className: "TopicCounter-used"
    }, used), this.suffix(max)), full ? m("span", {
      className: "TopicCounter-hint"
    }, app.translator.trans('ziiven-topics.forum.composer.limit_reached')) : null);
  }

  /**
   * The label is two pieces so the number can be emphasised while the rest
   * stays quiet. English puts the number first and Chinese puts it third,
   * so the prefix is empty in one of them.
   */
  prefix() {
    const prefix = app.translator.trans('ziiven-topics.forum.composer.used_prefix');
    return prefix ? m("span", {
      className: "TopicCounter-prefix"
    }, prefix, " ") : null;
  }
  suffix(max) {
    return app.translator.trans('ziiven-topics.forum.composer.used_suffix', {
      max
    });
  }
}
flarum.reg.add('ziiven-topics', 'forum/components/TopicCounter', TopicCounter);